/**
 * @fileoverview Hook for managing burger menu guidance on first visit
 */

import { useState, useEffect } from 'react';

const STORAGE_KEY = 'spinner-games-menu-guidance-shown';
const USER_PROFILE_KEY = 'menuGuidanceShown';

interface BurgerMenuGuidanceState {
  showGuidance: boolean;
  dismissGuidance: () => void;
  dismissGuidanceOnClick: () => void;
}

/**
 * Hook to manage burger menu guidance for first-time users
 * Shows a gentle pulse animation to guide users to the menu
 */
export const useBurgerMenuGuidance = (): BurgerMenuGuidanceState => {
  const [showGuidance, setShowGuidance] = useState(false);

  useEffect(() => {
    // Check if guidance has been shown before
    const hasSeenGuidance = localStorage.getItem(STORAGE_KEY) === 'true';
    
    if (!hasSeenGuidance) {
      // Show guidance after 2.5 seconds delay
      const guidanceTimer = setTimeout(() => {
        setShowGuidance(true);
        
        // Auto-dismiss after 10 seconds total
        const dismissTimer = setTimeout(() => {
          dismissGuidance();
        }, 10000);
        
        return () => {
          clearTimeout(dismissTimer);
        };
      }, 2500);
      
      return () => clearTimeout(guidanceTimer);
    }
  }, []);

  const dismissGuidance = () => {
    setShowGuidance(false);
    
    // Mark as shown in localStorage
    localStorage.setItem(STORAGE_KEY, 'true');
    
    // TODO: Also save to user profile if available
    // This would be implemented when user profile system is ready
    // saveToUserProfile(USER_PROFILE_KEY, true);
  };

  const dismissGuidanceOnClick = () => {
    if (showGuidance) {
      dismissGuidance();
    }
  };

  // No global click listener - only dismiss on burger menu click

  return {
    showGuidance,
    dismissGuidance,
    dismissGuidanceOnClick
  };
};
